import { defineStore } from 'pinia'
import { computed, ref, unref, watch } from 'vue'
import { useDevicesList, useUserMedia } from '@vueuse/core'
import { store } from './index'

export const useCameraStore = defineStore('APP_CAMERA', () => {
  // 基础状态
  const currentCamera = ref<ConstrainDOMString>()
  const permissionError = ref<string>('')
  const isInitialized = ref(false)

  // 设备列表
  const { videoInputs: cameras } = useDevicesList({
    requestPermissions: true,
    onUpdated() {
      // 如果当前没有选中摄像头，或者当前选中的摄像头不存在，则选中第一个
      if (!currentCamera.value || !cameras.value?.find((i) => i.deviceId === currentCamera.value)) {
        currentCamera.value = cameras.value[0]?.deviceId
      }
    }
  })

  const cameraList = computed(() => {
    return unref(cameras).map((item, index) => {
      return {
        ...item,
        name: `摄像头${index + 1}`,
        code: item.deviceId
      }
    })
  })

  const constraints = computed(() => {
    return {
      video: {
        deviceId: unref(currentCamera)
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    }
  })

  // 用户媒体流
  const { stream, restart, stop } = useUserMedia({
    constraints,
    enabled: false // 默认不启用，需要手动获取
  })

  // 检查媒体流状态
  const streamStatus = computed(() => {
    if (!stream.value) return 'inactive'
    const tracks = stream.value.getTracks()
    if (tracks.length === 0) return 'no-tracks'
    const videoTrack = tracks.find((track) => track.kind === 'video')
    if (!videoTrack) return 'no-video'
    return videoTrack.readyState
  })

  // 检查是否有活跃的视频轨道
  const hasActiveVideoTrack = computed(() => {
    if (!stream.value) return false
    const videoTrack = stream.value.getVideoTracks()[0]
    return videoTrack && videoTrack.readyState === 'live' && videoTrack.enabled
  })

  // 监听设备变化
  watch(
    cameraList,
    (val, oldVal) => {
      if (oldVal && oldVal.length === 0 && val.length > 0 && isInitialized.value) {
        console.log('检测到摄像头设备变化，重启媒体流')
        restart()
      }
    },
    {
      immediate: true,
      deep: true
    }
  )

  // 检查摄像头权限
  async function checkCameraPermission(): Promise<boolean> {
    try {
      const permission = await navigator.permissions.query({ name: 'camera' as PermissionName })
      if (permission.state === 'denied') {
        permissionError.value = '摄像头权限被拒绝，请在浏览器设置中允许访问摄像头'
        return false
      }
      permissionError.value = ''
      return true
    } catch (error) {
      console.warn('检查摄像头权限时出错:', error)
      return true // 不支持权限API时默认允许
    }
  }

  // 启动摄像头
  async function startCamera(): Promise<void> {
    console.log('[Camera Store] 启动摄像头')

    try {
      // 检查权限
      const hasPermission = await checkCameraPermission()
      if (!hasPermission) {
        throw new Error('摄像头权限被拒绝')
      }

      // 如果已经有活跃流，直接返回
      if (hasActiveVideoTrack.value) {
        console.log('[Camera Store] 摄像头已经正常运行')
        return
      }

      console.log('[Camera Store] 开始获取摄像头流')
      await restart()

      isInitialized.value = true
      console.log('[Camera Store] 摄像头启动成功')
    } catch (error: any) {
      console.error('[Camera Store] 摄像头启动失败:', error)

      // 处理不同类型的错误
      if (error?.name === 'NotAllowedError') {
        permissionError.value = '摄像头权限被拒绝'
      } else if (error?.name === 'NotFoundError') {
        permissionError.value = '未找到摄像头设备'
      } else if (error?.name === 'NotReadableError') {
        permissionError.value = '摄像头被其他应用占用或硬件错误'
      } else {
        permissionError.value = error?.message || '摄像头启动失败'
      }

      throw error
    }
  }

  // 停止摄像头
  async function stopCamera(): Promise<void> {
    console.log('[Camera Store] 停止摄像头')

    try {
      // 停止媒体流
      if (stream.value) {
        console.log('[Camera Store] 停止媒体流轨道')
        stream.value.getTracks().forEach((track) => {
          if (track.readyState !== 'ended') {
            track.stop()
          }
        })
      }

      // 调用 stop 方法
      stop()

      // 重置状态
      isInitialized.value = false
      permissionError.value = ''

      console.log('[Camera Store] 摄像头停止完成')
    } catch (error) {
      console.error('[Camera Store] 停止摄像头时出错:', error)
      // 即使出错也要重置状态
      isInitialized.value = false
    }
  }

  // 重启摄像头（用于切换设备或修复问题）
  async function restartCamera(): Promise<void> {
    console.log('[Camera Store] 重启摄像头')
    try {
      // 先停止
      await stopCamera()
      // 等待一小段时间
      await new Promise((resolve) => setTimeout(resolve, 100))
      // 重新启动
      await startCamera()
    } catch (error) {
      console.error('[Camera Store] 重启摄像头失败:', error)
      throw error
    }
  }

  // 检查摄像头状态
  function checkCameraStatus(): boolean {
    if (!stream.value) {
      console.log('[Camera Store] 检查状态: 没有媒体流')
      return false
    }

    const videoTrack = stream.value.getVideoTracks()[0]
    if (!videoTrack) {
      console.log('[Camera Store] 检查状态: 没有视频轨道')
      return false
    }

    const isGood = videoTrack.readyState === 'live' && videoTrack.enabled && !videoTrack.muted
    console.log('[Camera Store] 摄像头状态检查:', {
      readyState: videoTrack.readyState,
      enabled: videoTrack.enabled,
      muted: videoTrack.muted,
      label: videoTrack.label,
      isGood
    })

    return isGood
  }

  // 修复摄像头黑屏
  async function fixCameraBlackScreen(): Promise<void> {
    try {
      console.log('[Camera Store] 开始修复摄像头黑屏')
      await restartCamera()
      console.log('[Camera Store] 摄像头黑屏修复完成')
    } catch (error) {
      console.error('[Camera Store] 修复摄像头黑屏失败:', error)
      throw error
    }
  }

  return {
    // 状态
    stream: readonly(stream),
    cameraList,
    permissionError: readonly(permissionError),
    isInitialized: readonly(isInitialized),
    streamStatus,
    hasActiveVideoTrack,
    currentCamera,

    // 方法
    checkCameraPermission,
    startCamera,
    stopCamera,
    restartCamera,
    checkCameraStatus,
    fixCameraBlackScreen
  }
})

export function useCameraStoreWithOut() {
  return useCameraStore(store)
}
